import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

// 2024 FHFA conforming loan limit (baseline). High-cost counties go to $1,149,825.
const CONFORMING_LIMIT = 766550;
const HIGH_COST_LIMIT = 1149825;

export default function JumboLoanPage() {
    const [homePrice, setHomePrice] = useState(1250000);
    const [downPct, setDownPct] = useState(20);
    const [rate, setRate] = useState(6.85);
    const [years, setYears] = useState(30);
    const [propertyTax, setPropertyTax] = useState(1.1);
    const [insurance, setInsurance] = useState(2400);

    const result = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const down = (hp * dp) / 100;
        const loan = Math.max(0, hp - down);
        const pi = monthlyPayment(loan, r, y);
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const monthly = pi + tax + ins;
        const isJumbo = loan > CONFORMING_LIMIT;
        const exceedsHighCost = loan > HIGH_COST_LIMIT;
        return { monthly, pi, tax, ins, down, loan, isJumbo, exceedsHighCost, totalPaid: pi * y * 12 };
    }, [homePrice, downPct, rate, years, propertyTax, insurance]);

    return (
        <CalculatorShell
            slug="jumbo-loan"
            explainer={
                                <>
                    <h2>How this jumbo loan calculator works</h2>
                    <p>
                        A jumbo loan calculator estimates payments for mortgages that exceed the conforming
                        loan limits set by the FHFA — loans too large for Fannie Mae or Freddie Mac backing.
                        Use it if you're financing a high-value property and want to understand how jumbo
                        underwriting and rates affect your payment.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Conforming limits:</strong> $766,550 for most areas in 2024; up to $1,149,825 in designated high-cost counties — amounts above these are jumbo.</li>
                        <li><strong>Monthly payment:</strong> uses the same amortization formula as any fixed-rate mortgage; the difference is in underwriting requirements.</li>
                        <li><strong>Rates:</strong> jumbo rates can be slightly higher or lower than conforming depending on market conditions and lender appetite.</li>
                        <li><strong>Down payment:</strong> typically 10–20% minimum; requirements are stricter than conforming loans.</li>
                        <li><strong>Reserves:</strong> lenders typically require 6–12 months of payments in liquid assets after closing.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>A credit score of 700 is the typical floor for jumbo loans; 740 or higher is required for the best rates.</li>
                        <li>Shop multiple lenders — jumbo rate variation is wider than conforming, so comparison shopping pays off more here.</li>
                        <li>Consider whether a piggyback loan structure (e.g., 80/10/10) keeps your first mortgage under the conforming limit and potentially qualifies you for better rates.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Home price" prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} step={0.5} dataTestId="calc-input-down" hint="20% typical" />
                        <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                        <Field label="Property tax" suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.05} dataTestId="calc-input-tax" />
                    </div>
                    <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                    <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                        <span>Down: {fmtUSD(result.down)}</span>
                        <span>Loan: {fmtUSD(result.loan)}</span>
                    </div>
                    <div
                        className={`text-sm p-3 rounded-md border ${
                            result.isJumbo
                                ? "bg-primary/5 border-primary/20 text-primary"
                                : "bg-accent/10 border-accent/30 text-primary"
                        }`}
                        data-testid="jumbo-status"
                    >
                        {result.isJumbo ? (
                            <>
                                <strong>Jumbo loan:</strong> {fmtUSD(result.loan, { decimals: 0 })} exceeds the{" "}
                                {fmtUSD(CONFORMING_LIMIT, { decimals: 0 })} baseline conforming limit.
                                {result.exceedsHighCost && (
                                    <> This is also above the $1.15M high-cost ceiling — expect extra scrutiny and a higher rate.</>
                                )}
                            </>
                        ) : (
                            <>
                                <strong>Still conforming:</strong> at {fmtUSD(result.loan, { decimals: 0 })}, this loan is
                                <em> below</em> the {fmtUSD(CONFORMING_LIMIT, { decimals: 0 })} baseline limit — a conventional
                                loan will likely be cheaper. Bump up the price or lower your down payment to see jumbo math.
                            </>
                        )}
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Monthly payment"
                        value={result.monthly}
                        format={(n) => fmtUSD(n, { decimals: 0 })}
                        secondary={[
                            { label: "P&I", value: result.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                            { label: "Tax + Ins", value: result.tax + result.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                            { label: "Total paid", value: result.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                            { label: "Total interest", value: result.totalPaid - result.loan, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
